import { Pages, MessageTrigger, TEMPLATE_NAME } from '../constants/enums';
import useLocalization from './useLocalization';
import { FormFieldsVariables } from '../models/app';
import { capitalizeSentence } from '../helpers/utils';
import 'react-quill/dist/quill.snow.css';
import { useSelector } from 'react-redux';
import React from 'react';
import MessageTemplateHelp from '../components/UI/MessageTemplateHelp/MessageTemplateHelp';

const useFormFields = (params: FormFieldsVariables) => {
  const conceptsListing = useSelector((state: any) => state.concepts.listing);
  const options = conceptsListing.map((concept: any) => ({
    value: concept.id,
    label: concept.name,
  }));
  const {
    slug,
    model,
    languagesOptions,
    featuresOptions,
    flagsOptions,
    interestsOptions,
    adminsOptions,
    adminRolesOptions,
    adminsCognitoGroups,
  } = params;
  const { strings } = useLocalization();
  const accountsSelected = useSelector((state: any) => state.accounts.selected);
  const featuresAllListing = useSelector(
    (state: any) => state.features.listingAll,
  );

  function checkFeature(slug: any): Boolean {
    let feature: any[] = [];
    if (accountsSelected && accountsSelected.features) {
      feature = featuresAllListing.filter(function (feature: any) {
        return (
          slug.toLowerCase() === feature.slug.toLowerCase() &&
          accountsSelected.features.indexOf(feature.id) !== -1
        );
      });
    }
    if (feature.length > 0) {
      return true;
    }

    return false;
  }

  const loginFields = () => [
    {
      name: 'email',
      label: strings.email,
      type: 'textField',
      autoFocus: true,
      defaultValue: '',
    },
    {
      name: 'password',
      label: strings.password,
      type: 'password',
      autoFocus: false,
      defaultValue: '',
    },
  ];

  const registerFields = () => [
    {
      name: 'name',
      label: 'Full Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: '',
    },
    {
      name: 'email',
      label: strings.email,
      type: 'textField',
      autoFocus: false,
      defaultValue: '',
    },
    {
      name: 'password',
      label: strings.password,
      type: 'password',
      autoFocus: false,
      defaultValue: '',
    },
  ];

  const adminFields = () => [
    {
      name: 'name',
      label: 'Full Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: '',
    },
    {
      name: 'email',
      label: strings.email,
      type: 'textField',
      autoFocus: false,
      defaultValue: '',
    },
    {
      name: 'password',
      label: strings.password,
      type: 'password',
      autoFocus: false,
      defaultValue: '',
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'password',
      autoFocus: false,
      defaultValue: '',
    },
    {
      name: 'group',
      label: 'Admin Group',
      type: 'select',
      autoFocus: false,
      defaultValue:
        slug === Pages.ADMINS
          ? adminsCognitoGroups[0].value
          : adminsCognitoGroups[1].value,
      options: adminsCognitoGroups,
    },
  ];

  const forgetPasswordFields = () => [
    {
      name: 'email',
      label: strings.email,
      type: 'textField',
      autoFocus: true,
      defaultValue: '',
    },
  ];

  const resetPasswordFields = () => [
    {
      name: 'email',
      label: strings.email,
      type: 'textField',
      autoFocus: true,
      defaultValue: '',
    },
    {
      name: 'code',
      label: strings.code,
      type: 'textField',
      autoFocus: false,
      defaultValue: '',
    },
    {
      name: 'newPassword',
      label: strings.password,
      type: 'password',
      autoFocus: false,
      defaultValue: '',
    },
  ];

  const accountFields = () => [
    {
      name: 'domain',
      label: 'Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.domain : '',
    },
    {
      name: 'siteTitle',
      label: 'Site Title',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.siteTitle : '',
    },
    {
      name: 'tagline',
      label: 'Tagline',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.tagline : '',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.description : '',
    },
    {
      name: 'siteAddress',
      label: 'Site Address',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.siteAddress : '',
    },
    {
      name: 'defaultLanguage',
      label: 'Default Language',
      type: 'select',
      autoFocus: false,
      defaultValue: model
        ? model.defaultLanguage
        : languagesOptions
          ? languagesOptions[0].value
          : '-1',
      options: languagesOptions,
    },
  ];

  const adminRoleFields = () => [
    {
      name: 'name',
      label: 'Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.name : '',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.description : '',
    },
  ];

  const adminGroupsFields = () => [
    {
      name: 'name',
      label: 'Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.name : '',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.description : '',
    },
  ];

  const GuestGroupsFields = () => [
    {
      name: 'name',
      label: 'Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.name : '',
    },
    {
      name: 'color',
      label: 'Color',
      type: 'color',
      autoFocus: true,
      defaultValue: model ? model.color : '',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.description : '',
    },
  ];

  const conceptFields = () => {
    let hasFeature = checkFeature('waiterorder');
    return [
      {
        name: 'name',
        label: 'Concept Name',
        type: 'textField',
        autoFocus: true,
        defaultValue: model ? model.name : '',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.description : '',
      },
      {
        name: 'precedence',
        label: 'Precedence',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.precedence : '',
      },
      // Concept Contacts
      {
        name: 'contactWhatsApp',
        label: 'WhatsApp Contact',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.contactWhatsApp || '' : '',
      },
      {
        name: 'contactPhoneNumber',
        label: 'Phone Number Contact',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.contactPhoneNumber || '' : '',
      },
      {
        name: 'contactEmail',
        label: 'Email Contact',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.contactEmail || '' : '',
      },

      // WhatsApp Auto-Reply Toggle
      {
        name: 'autoReplyEnabled',
        label: 'Enable WhatsApp Auto-Reply',
        type: 'checkbox',
        autoFocus: false,
        defaultValue:
          model && model.autoReplyEnabled !== undefined && model.autoReplyEnabled !== null
            ? model.autoReplyEnabled
            : false,
      },
      {
        name: 'autoReplyText',
        label: 'WhatsApp Auto-Reply Message',
        type: 'textarea',
        autoFocus: false,
        defaultValue: model ? model.autoReplyText || '' : '',
      },

      // OHIP Integration Credentials Section
      // --- UI ONLY: commented out so the OHIP fields are hidden from the form.
      // --- To show them again, just uncomment this block. No backend/data changes needed.
      /*
      {
        name: 'enableOhipIntegration',
        label: 'Enable OHIP Integration',
        type: 'checkbox',
        autoFocus: false,
        defaultValue:
          model && model.enableOhipIntegration !== undefined
            ? model.enableOhipIntegration
            : false,
      },

      {
        name: 'ohipClientId',
        label: 'OHIP Client ID',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipClientId || '' : '',
      },
      {
        name: 'ohipClientSecret',
        label: 'OHIP Client Secret',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipClientSecret || '' : '',
      },
      {
        name: 'ohipAuthUrl',
        label: 'OHIP Auth URL',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipAuthUrl || '' : '',
      },
      {
        name: 'ohipApiBaseUrl',
        label: 'OHIP API Base URL',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipApiBaseUrl || '' : '',
      },
      {
        name: 'ohipHotelId',
        label: 'OHIP Hotel ID',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipHotelId || '' : '',
      },
      {
        name: 'ohipAppKey',
        label: 'OHIP App Key',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipAppKey || '' : '',
      },
      {
        name: 'ohipEnterpriseId',
        label: 'OHIP Enterprise ID',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipEnterpriseId || '' : '',
      },
      {
        name: 'ohipExternalSystem',
        label: 'OHIP External System',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipExternalSystem || '' : '',
      },
      {
        name: 'ohipScope',
        label: 'OHIP Scope',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipScope || '' : '',
      },
      {
        name: 'ohipUsername',
        label: 'OHIP Username',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipUsername || '' : '',
      },
      {
        name: 'ohipPassword',
        label: 'OHIP Password',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipPassword || '' : '',
      },
      {
        name: 'ohipGrantType',
        label: 'OHIP Grant Type',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.ohipGrantType || '' : '',
      },
      */

      // Email & WhatsApp Integration Credentials
      // --- UI ONLY: commented out so these fields are hidden from the form.
      // --- To show them again, just uncomment this block. No backend/data changes needed.
      /*
      // Email Integration Credentials
      {
        name: 'senderEmail',
        label: 'Sender Email',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.senderEmail || '' : '',
      },

      // WhatsApp Integration Credentials
      {
        name: 'whatsappToken',
        label: 'WhatsApp Token',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.whatsappToken || '' : '',
      },
      {
        name: 'whatsappApiUrl',
        label: 'WhatsApp API URL',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.whatsappApiUrl || '' : '',
      },
      {
        name: 'whatsappPhoneId',
        label: 'WhatsApp Phone ID',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.whatsappPhoneId || '' : '',
      },
      */
      hasFeature
        ? {
            name: 'WaiterNotificationOption',
            label: 'Waiter Notification Options',
            type: 'select',
            autoFocus: false,
            defaultValue: model
              ? model.WaiterNotificationOption
              : 'All Waiters',
            options: [
              { label: 'All Waiters', value: 'All Waiters' },
              { label: 'Same Waiter', value: 'Same Waiter' },
              { label: 'Runner', value: 'Runner' },
            ],
          }
        : { name: '1' },
      hasFeature
        ? {
            name: 'isPinRequired',
            label: 'Require QR Code Pin',
            type: 'checkbox',
            autoFocus: false,
            defaultValue: model ? model.isPinRequired : false,
          }
        : { name: '2' },
      hasFeature
        ? {
            name: 'needAssistance',
            label: 'Need Assistance',
            type: 'checkbox',
            autoFocus: false,
            defaultValue: model ? model.needAssistance : false,
          }
        : { name: '3' },
      hasFeature
        ? {
            name: 'spaBooking',
            label: 'Spa Booking',
            type: 'checkbox',
            autoFocus: false,
            defaultValue: model ? model.spaBooking : false,
          }
        : { name: '4' },
      hasFeature
        ? {
            name: 'showerBooking',
            label: 'Shower Booking',
            type: 'checkbox',
            autoFocus: false,
            defaultValue: model ? model.showerBooking : false,
          }
        : { name: '5' },
      hasFeature
        ? {
            name: 'cigarMenu',
            label: 'Cigar Menu',
            type: 'checkbox',
            autoFocus: false,
            defaultValue: model ? model.cigarMenu : false,
          }
        : { name: '6' },
      hasFeature
        ? {
            name: 'viewOnly',
            label: 'View Only',
            type: 'checkbox',
            autoFocus: false,
            defaultValue: model ? model.viewOnly : false,
          }
        : { name: '7' },
    ];
  };

  const payMobFields = () => [
    {
      name: 'merchantID',
      label: 'Merchant ID', //this is paymob ID
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.merchantID : '',
    },
    {
      name: 'merchantIntegrationNumber',
      label: 'Integration Number',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.merchantIntegrationNumber : '',
    },
    {
      name: 'merchantAPIKey',
      label: 'Merchant API Key',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.merchantAPIKey : '',
    },
  ];

  const preparationAreaFields = () => [
    {
      name: 'name',
      label: 'Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? capitalizeSentence(model.name) : '',
    },
    {
      name: 'precedence',
      label: 'Precedence',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.precedence : '',
    },
    {
      name: 'isAutoAccept',
      label: 'Auto Accept',
      type: 'checkbox',
      autoFocus: false,
      defaultValue: model ? model.isAutoAccept : false,
    },
    {
      name: 'hasKDS',
      label: 'KDS',
      type: 'checkbox',
      autoFocus: false,
      defaultValue: model ? model.hasKDS : false,
    },
    {
      name: 'hasPrinter',
      label: 'Printer',
      type: 'checkbox',
      autoFocus: false,
      defaultValue: model ? model.hasPrinter : false,
    },
  ];

  const featureFields = () => [
    {
      name: 'precedence',
      label: 'Precedence',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.precedence : '',
    },
    {
      name: 'name',
      label: 'Feature Name',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.name : '',
    },
    {
      name: 'icon',
      label: 'Feature Icon',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.icon : '',
    },
    {
      name: 'slug',
      label: 'Feature Slug',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.slug : '',
    },
    {
      name: 'parent',
      label: 'Parent',
      type: 'select',
      autoFocus: false,
      defaultValue: model
        ? model.parent
          ? model.parent
          : featuresOptions
            ? featuresOptions[0].value
            : '-1'
        : featuresOptions
          ? featuresOptions[0].value
          : '-1',
      options: featuresOptions,
    },
    {
      name: 'private',
      label: 'Private',
      type: 'checkbox',
      autoFocus: false,
      defaultValue: model ? model.private : false,
    },
  ];

  const languageFields = () => [
    {
      name: 'name',
      label: 'Language Name',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.name : '',
    },
    {
      name: 'code',
      label: 'Language Code',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.code : '',
    },
  ];

  const userCheckboxes = () => [
    {
      name: 'interests',
      label: 'Interests',
      type: 'checkboxes',
      autoFocus: false,
      defaultValue: new Set(),
      options: interestsOptions,
    },
    {
      name: 'flags',
      label: 'Flags',
      type: 'checkboxes',
      autoFocus: false,
      defaultValue: new Set(),
      options: flagsOptions,
    },
  ];

  const accountCheckboxes = () => {
    return [
      {
        name: 'languages',
        label: 'Languages',
        type: 'checkboxes',
        autoFocus: false,
        defaultValue: model ? new Set(model.languages) : new Set(),
        options: languagesOptions,
      },
      {
        name: 'features',
        label: 'Features',
        type: 'checkboxes',
        autoFocus: false,
        defaultValue: model ? new Set(model.features) : new Set(),
        options: featuresOptions,
      },
    ];
  };

  const adminGroupsCheckboxes = () => [
    {
      name: 'roles',
      label: 'Admin Roles',
      type: 'checkboxes',
      autoFocus: false,
      defaultValue: model ? new Set(model.roles) : new Set(),
      options: adminRolesOptions,
    },
    {
      name: 'users',
      label: 'Admin Users',
      type: 'checkboxes',
      autoFocus: false,
      defaultValue: model ? new Set(model.users) : new Set(),
      options: adminsOptions,
    },
  ];

  const bookingConfirmationFields = () => [
    {
      name: 'email',
      type: 'textField',
      autoFocus: true,
      defaultValue: '',
    },
  ];

  const transactionFields = () => [
    {
      name: 'transactionID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.transactionID : '',
    },
    {
      name: 'guestName',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.guestName : '',
    },
    {
      name: 'guestPhone',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.guestPhone : '',
    },
    {
      name: 'guestID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.guestID : '',
    },
    {
      name: 'guestGroup',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.guestGroup : '',
    },
    {
      name: 'bookingID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.bookingID : '',
    },
    {
      name: 'amount_cents',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.amount_cents : '',
    },
    {
      name: 'status',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.status : '',
    },
    {
      name: 'type',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.type : '',
    },
    {
      name: 'timeSlots',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.timeSlots : '',
    },
    {
      name: 'tables',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.tables : '',
    },
    {
      name: 'conceptID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.conceptID : '',
    },
    {
      name: 'currency',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.currency : '',
    },
    {
      name: 'refund',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.refund : '',
    },

    {
      name: 'orderID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.orderID : '',
    },
    {
      name: 'ownerID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.ownerID : '',
    },
    {
      name: 'refunded_amount_cents',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.refunded_amount_cents : '',
    },
  ];

  const paymentStatFields = () => [
    {
      name: 'accountID',
      type: 'textField',
      autoFocus: true,
      defaultValue: model ? model.accountID : '',
    },
    {
      name: 'year',
      type: 'number',
      autoFocus: true,
      defaultValue: model ? model.year : '',
    },
    {
      name: 'month',
      type: 'number',
      autoFocus: true,
      defaultValue: model ? model.month : 1,
    },
    {
      name: 'earning',
      type: 'number',
      autoFocus: true,
      defaultValue: model ? model.earning : '',
    },
    {
      name: 'refund',
      type: 'number',
      autoFocus: true,
      defaultValue: model ? model.refund : '',
    },
    {
      name: 'earningTrxCount',
      type: 'number',
      autoFocus: true,
      defaultValue: model ? model.earningTrxCount : '',
    },
    {
      name: 'refundTrxCount',
      type: 'number',
      autoFocus: true,
      defaultValue: model ? model.refundTrxCount : 0,
    },
  ];

  const ParentConceptsFields = () => [
    {
      name: 'name',
      label: 'Name',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.name : '',
    },
    {
      name: 'type',
      label: 'Type',
      type: 'textField',
      autoFocus: false,
      defaultValue: model ? model.type : '',
    },
    {
      name: 'image',
      label: 'Image',
      type: 'upload',
      autoFocus: false,
      defaultValue: model ? model.image : '',
    },
    {
      name: 'logo',
      label: 'Logo',
      type: 'upload',
      autoFocus: false,
      defaultValue: model ? model.logo : '',
    },
    {
      name: 'venues',
      label: 'Venues',
      type: 'checkboxes',
      autoFocus: false,
      defaultValue: model ? new Set(model.venues) : new Set(),
      options: options,
    },
  ];

  const messagingFields = () => {
    // Convert MessageTrigger enum to options array
    const triggerOptions = Object.values(MessageTrigger).map((value) => ({
      label: value
        .split('_')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' '),
      value: value,
    }));

    // Convert TEMPLATE_NAME enum to options array
    const templateNameOptions = Object.values(TEMPLATE_NAME).map((value) => ({
      label: value
        .split('_')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' '),
      value: value,
    }));

    return [
      {
        name: 'trigger',
        label: 'Trigger',
        type: 'select',
        autoFocus: true,
        defaultValue: model ? model.trigger : MessageTrigger.CHECK_IN,
        options: triggerOptions,
      },
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        autoFocus: false,
        defaultValue: model ? model.channel : 'whatsapp',
        options: [
          // { label: 'SMS', value: 'sms' },
          { label: 'WhatsApp', value: 'whatsapp' },
          { label: 'Email', value: 'email' },
        ],
      },
      // WhatsApp
      {
        name: 'templateName',
        label: 'Template Name',
        type: 'select',
        autoFocus: false,
        defaultValue: model
          ? model.templateName
          : TEMPLATE_NAME.GREETING_MESSAGE,
        options: templateNameOptions,
      },
      // Email
      {
        name: 'subject',
        label: 'Subject',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.subject : '',
      },
      {
        name: 'header',
        label: 'Header',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.header : '',
      },
      // Message Body
      {
        name: 'messageTemplate',
        label: 'Message Template',
        type: 'textarea',
        autoFocus: false,
        defaultValue: model ? model.messageTemplate : '',
        helperText:
          'Available placeholders: {guestName}, {reservationId}, {arrivalDate}, {departureDate}, {conceptName}, {confirmationNumber}, {status}. Press Enter for new lines.',
        helpContent: React.createElement(MessageTemplateHelp),
      },
      {
        name: 'footer',
        label: 'Footer',
        type: 'textarea',
        autoFocus: false,
        defaultValue: model ? model.footer : '',
      },
      {
        name: 'isActive',
        label: 'Active',
        type: 'checkbox',
        autoFocus: false,
        defaultValue:
          model && model.isActive !== undefined ? model.isActive : true,
      },
      {
        name: 'sendRatingAfter',
        label: 'Send Rating After',
        type: 'checkbox',
        autoFocus: false,
        defaultValue:
          model && model.sendRatingAfter !== undefined
            ? model.sendRatingAfter
            : false,
      },

      // {
      //   name: 'delayMinutes',
      //   label: 'Delay (Minutes)',
      //   type: 'number',
      //   autoFocus: false,
      //   defaultValue: model ? model.delayMinutes : 0,
      // },
    ];
  };

  const conversationsFields = () => {
    return [
      {
        name: 'guestName',
        label: 'Guest Name',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.guestName : '',
      },
      {
        name: 'guestEmail',
        label: 'Guest Email',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.guestEmail : '',
      },
      {
        name: 'lastMessagePreview',
        label: 'Message',
        type: 'textarea',
        autoFocus: false,
        defaultValue: model ? model.lastMessagePreview : '',
      },
      {
        name: 'confirmationNumber',
        label: 'Confirmation Number',
        type: 'textField',
        autoFocus: false,
        defaultValue: model ? model.confirmationNumber : '',
      },
    ];
  };

  const getPayMobFields = (
    numToUpgradeWatch?: any,
    numToDowngradeWatch?: any,
    channelsWatch?: any,
  ) => {
    return payMobFields();
  };

  const getFormFields = (
    numToUpgradeWatch?: any,
    numToDowngradeWatch?: any,
    channelsWatch?: any,
  ) => {
    switch (slug) {
      case Pages.LOGIN:
        return loginFields();
      case Pages.REGISTER:
        return registerFields();
      case Pages.ADMINS:
        return adminFields();
      case Pages.STAFF_MEMBERS:
        return adminFields();
      case Pages.FORGOT_PASSWORD:
        return forgetPasswordFields();
      case Pages.RESET_PASSWORD:
        return resetPasswordFields();
      case Pages.ACCOUNTS:
        return accountFields();
      case Pages.CONCEPTS:
        return conceptFields();
      case Pages.Parent_Concepts:
        return ParentConceptsFields();
      case Pages.FEATURES:
        return featureFields();
      case Pages.LANGUAGES:
        return languageFields();
      case Pages.BOOKING_CONFIRMATION:
        return bookingConfirmationFields();
      case Pages.ADMIN_ROLES:
        return adminRoleFields();
      case Pages.ADMIN_GROUPS:
        return adminGroupsFields();
      case Pages.GUEST_GROUP:
        return GuestGroupsFields();
      case Pages.TRANSACTIONS_REPORT:
        return transactionFields();
      case Pages.PAYMENT_STATS:
        return paymentStatFields();
      case Pages.MESSAGING:
        return messagingFields();
      case Pages.CONVERSATIONS:
        return conversationsFields();
      default:
        return [];
    }
  };

  const getCheckboxesFields = () => {
    switch (slug) {
      case Pages.ACCOUNTS:
        return accountCheckboxes();
      case Pages.GUESTS:
        return userCheckboxes();
      case Pages.ADMIN_GROUPS:
        return adminGroupsCheckboxes();
      default:
        return [];
    }
  };

  const generalImages = () => [
    {
      name: 'image',
      label: 'Image',
      type: 'image',
      autoFocus: false,
      defaultValue: model ? model.image : '',
    },
  ];
  const generalPdfs = () => [
    {
      name: 'pdf',
      label: 'pdf',
      type: 'image',
      autoFocus: false,
      defaultValue: model ? model.menuPDF : '',
    },
  ];

  const getImageFields = () => {
    switch (slug) {
      case Pages.MENU_ITEMS:
      case Pages.CATEGORIES:
      case Pages.MAIN_CATEGORIES:
      case Pages.SUB_CATEGORIES:
      case Pages.TABLES:
        return generalImages();

      default:
        return [];
    }
  };
  const getPdfFields = () => {
    switch (slug) {
      case Pages.MAIN_CATEGORIES:
      case Pages.SUB_CATEGORIES:
        return generalPdfs();
      default:
        return [];
    }
  };

  return {
    getFormFields,
    getPayMobFields,
    getCheckboxesFields,
    getImageFields,
    getPdfFields,
  };
};

export default useFormFields;
